import Vue from 'vue'
import { FORM_EVENTS, FORM_STATUS } from '@shared/const/form/form.status'
import { formNormalize } from './form.normalize'
import { formValidate } from './form.validate'
import { formSubmit as sendForm } from './form.submit'
import { FORM_FIELD_PROPS, FORM_FIELD_VIEW, resolveDisableState, resolveFieldValue } from '@shared/const/form/form.field'
import { setByPath } from '@shared/utils/object'

export function formController(options = {}) {
  const form = formNormalize(options)

  return {
    method: options.method,
    formContext: options.formContext,
    isStepMode: form.isStepMode,
    state: Vue.observable({
      form,
      status: FORM_STATUS.INIT,
      currentStep: 0,
      fields: form.fields,
      errors: {},
      submitError: null,
      dadataLocation: null,
      maximum: {
        employee: null,
        partner: null,
      },
    }),
    get publicApi() {
      return {
        formId: () => this.formId,
        isStepMode: () => this.isStepMode,
        setStatus: status => this.setStatus(status),
        reset: () => this.reset(),
        visibleFields: this.visibleFields,
      }
    },
    get formId() {
      return `form-${this.state.form.id}`
    },
    get isFirstStep() {
      return this.state.currentStep === 0
    },
    get isLastStep() {
      if (!this.isStepMode) {
        return true
      }

      const stepsCount = this.state.fields.length

      if (stepsCount === 0) {
        return true
      }

      return this.state.currentStep >= stepsCount - 1
    },
    get visibleFields() {
      if (this.isStepMode) {
        return this.state.fields[this.state.currentStep] || []
      }

      return this.state.fields || []
    },
    get allFields() {
      return this.isStepMode ? (this.state.fields || []).flat(1) : this.state.fields || []
    },
    async callEvent(event, payload = {}) {
      switch (event) {
        case FORM_EVENTS.INIT:
          this.setStatus(FORM_STATUS.INIT)

          if (typeof options.onFormInit === 'function') {
            options.onFormInit({ formState: this.state, publicApi: this.publicApi })
          }
          break

        case FORM_EVENTS.FIELD_INPUT:
          this.onFieldInput(payload.field, payload.value)
          break

        case FORM_EVENTS.FIELD_BLUR:
          // this.onFieldBlur(payload.field)
          break

        case FORM_EVENTS.FIELD_FOCUS:
          this.onFieldFocus(payload)
          break

        case FORM_EVENTS.FIELD_ERROR:
          const field = payload.field

          this.onFieldError(field)

          if (typeof options.onFieldError === 'function') {
            options.onFieldError({
              formId: this.formId,
              field,
            })
          }
          break

        case FORM_EVENTS.BACK_STEP:
          this.backStep()

          if (typeof options.onBackStep === 'function') {
            options.onBackStep({
              currentStep: this.state.currentStep,
              formId: this.formId,
            })
          }
          break

        case FORM_EVENTS.NEXT_STEP:
          const isValid = await this.nextStep()

          if (isValid) {
            if (typeof options.onNextStep === 'function') {
              options.onNextStep({
                currentStep: this.state.currentStep,
                formId: this.formId,
                isValid: isValid,
              })
            }
          }

          return isValid

        case FORM_EVENTS.VALIDATION:
          this.setStatus(FORM_STATUS.VALIDATION)
          return await this.validate()

        case FORM_EVENTS.VALIDATION_ERROR:
          this.setStatus(FORM_STATUS.VALIDATION_ERROR)

          if (typeof options.onValidationError === 'function') {
            options.onValidationError({
              formId: this.formId,
            })
          }
          break

        case FORM_EVENTS.SUBMIT:
          this.setStatus(FORM_STATUS.SUBMITTING)

          if (typeof options.onSubmitting === 'function') {
            options.onSubmitting({
              recaptchaToken: payload.recaptchaToken,
            })
          }
          return this.submit(payload.recaptchaToken)

        case FORM_EVENTS.SUBMIT_SUCCESS:
          this.setStatus(FORM_STATUS.SUCCESS)

          if (typeof options.onSuccessSubmit === 'function') {
            options.onSuccessSubmit({ formId: this.formId, response: payload.response })
          }
          this.onSuccessSubmit(payload)

          break

        case FORM_EVENTS.SUBMIT_ERROR:
          this.setStatus(FORM_STATUS.ERROR)

          if (typeof options.onErrorSubmit === 'function') {
            options.onErrorSubmit({ formId: this.formId })
          }
          this.onErrorSubmit({ error: payload.error })

          break

        case FORM_EVENTS.RESET:
          this.reset()
          this.setStatus(FORM_STATUS.INIT)

          if (typeof options.onFormReset === 'function') {
            options.onFormReset()
          }
          break

        default:
          console.warn(`Unknown form event: ${event}`)
      }
    },
    setStatus(status) {
      this.state.status = status
    },
    onFieldInput(field, value) {
      field.value = value

      if (field.type === FORM_FIELD_VIEW.DADATA_CITY) {
        this.state.dadataLocation = value
      }

      const contextFormPath = field.addParams?.fieldProps?.[FORM_FIELD_PROPS.contextFormPath]
      if (contextFormPath) {
        setByPath(this.state, contextFormPath, value)
      }

      field.error = false
      field.errorText = null

      Vue.delete(this.state.errors, field.id)

      this.setStatus(FORM_STATUS.EDITING)
    },
    onFieldFocus({ field }) {
      field.error = false

      Vue.delete(this.state.errors, field.id)

      this.setStatus(FORM_STATUS.EDITING)
    },
    async validateFields(fields) {
      let isValid = true

      for (const field of fields) {
        const result = await formValidate(field, this.state.form)

        if (!result.valid) {
          Vue.set(this.state.errors, field.id, result.message)
          isValid = false
          await this.callEvent(FORM_EVENTS.FIELD_ERROR, { field })
        } else {
          Vue.delete(this.state.errors, field.id)
        }
      }

      return isValid
    },
    async validate() {
      const isValid = await this.validateFields(this.visibleFields)

      if (this.isStepMode && !this.isLastStep) {
        if (!isValid) await this.callEvent(FORM_EVENTS.VALIDATION_ERROR)

        return isValid
      }

      if (isValid) {
        return true
      }

      await this.callEvent(FORM_EVENTS.VALIDATION_ERROR)

      return false
    },
    backStep() {
      if (this.state.currentStep <= 0) return

      this.state.currentStep -= 1
      this.setStatus(FORM_STATUS.EDITING)
    },
    async nextStep() {
      if (!this.isStepMode || this.isLastStep) return false

      const isValid = await this.validate()

      if (!isValid) {
        return false
      }

      this.state.currentStep += 1
      this.setStatus(FORM_STATUS.EDITING)

      return true
    },
    async submit(recaptchaToken) {
      try {
        const response = await sendForm(this.method, recaptchaToken, this.state.form, this.allFields, options)

        if (!response.success) {
          throw response
        }

        await this.callEvent(FORM_EVENTS.SUBMIT_SUCCESS, { response })
        return response
      } catch (error) {
        console.error('Submit error:', error)
        await this.callEvent(FORM_EVENTS.SUBMIT_ERROR, { error })
        return false
      }
    },
    onSuccessSubmit() {
      //
    },
    onErrorSubmit({ error }) {
      this.state.submitError = error

      if (error?.errors) {
        Object.keys(error.errors).forEach(fieldId => {
          const field = this.allFields.find(item => String(item.id) === String(fieldId))

          if (!field) return

          const message = Array.isArray(error.errors[fieldId]) ? error.errors[fieldId][0] : error.errors[fieldId]

          field.error = true
          field.valid = false
          field.errorText = message
        })
      }
    },
    onFieldError(field) {
      if (field.error && field.disabled) {
        field.disabled = false
      }
    },
    reset() {
      this.state.currentStep = 0
      this.state.submitError = null

      this.state.errors = {}

      this.allFields.forEach(field => {
        field.error = false
        field.valid = false
        field.errorText = null

        field.disabled = resolveDisableState(field, this.formContext)

        if (!field.disabled) {
          field.value = null
          field.value = resolveFieldValue(field, this.formContext)
        }
      })

      this.state.dadataLocation = null
      this.state.maximum.employee = null
      this.state.maximum.partner = null
    },
  }
}



// WEBPACK FOOTER //
// ./src/modules/shared/services/form/form.controller.js